import { Button } from '@pelckmans/business-components/components/button';
import { Dialog, ModalDialog } from '@pelckmans/business-components/components/dialog';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../contexts/ModalContext';
import { Node } from '../_api/TableOfContentsApi';
import MaterialModalHeader from './common/MaterialModalHeader';
import ModuleContext from '../_contexts/ModuleContext';
import PdfjsLinkTargets from '../../../constants/PdfjsLinkTargets';

type PdfPreviewModalProps = {
  id: string;
  downloadUrl?: string;
  downloadDisclaimer?: JSX.Element;
  previewUrl?: string;
  node: Node;
  contentType: ReactNode;
  url: string;
  name: string;
  info: ReactNode;
  linkTarget?: 'new-window' | 'same-window';
  tutorialId?: string;
};

export default function PdfPreviewModal({
  id,
  downloadUrl,
  downloadDisclaimer,
  url,
  name,
  info,
  node,
  contentType,
  previewUrl,
  linkTarget = 'new-window',
  tutorialId,
}: PdfPreviewModalProps) {
  const { t } = useTranslation();

  const { closeModal } = useContext(ModalContext);
  const { selectedModule } = useContext(ModuleContext);

  const handleDownload = () => {
    window.open(downloadUrl, '_blank');
  };

  const showPreviewLink = Boolean(selectedModule?.licenses.hasDigitalManualLicenseForTeacher && previewUrl);

  const showDownloadInfo = Boolean(downloadUrl || previewUrl || downloadDisclaimer);

  return (
    <ModalDialog
      id={id}
      closeViaEscape={!tutorialId}
      closeOnClickOutside={!tutorialId}
      closeTitle={t('ACTIONS.CLOSE')}
      onClose={() => closeModal(id)}
      className="material-dialog"
      fullScreen
    >
      <MaterialModalHeader
        title={name}
        description={node.description}
        contentType={contentType}
        tutorialId={tutorialId}
      />
      <Dialog.Body>
        <div className="information">
          <p className="name">{name}</p>
          {info}
          {showDownloadInfo && (
            <>
              <hr />
              {downloadDisclaimer ?? (
                <div className="material-dialog__buttons">
                  {showPreviewLink && (
                    <a
                      className="bc-button__button bc-button__small bc-button__primary bc-button__outline"
                      href={previewUrl}
                    >
                      <svg className="bc-icon bc-icon--left icon-open-p">
                        <use href="#icon-open-p" />
                      </svg>
                      <span>{t('MODULES.MATERIAL.COMPONENTS.WHITEPAGE-PREVIEW-MODAL.ACTIONS.OPEN')}</span>
                    </a>
                  )}
                  {downloadUrl && (
                    <Button
                      onClick={handleDownload}
                      variant="primary"
                      size={showPreviewLink ? 'small' : undefined}
                      outline={showPreviewLink}
                    >
                      <i className="bc-icon bc-icon-arrow-down bc-icon--left" />
                      <span>{t('ACTIONS.DOWNLOAD')}</span>
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <object
          className="pdf bc-shimmer"
          type="text/html"
          data={`${process.env.PUBLIC_URL}/pdfjs/web/viewer.html?file=${encodeURIComponent(url)}&externallinktarget=${
            PdfjsLinkTargets[linkTarget]
          }`}
          aria-label="pdf-preview"
          title="pdf-preview"
          data-testid="pdf-preview"
        />
      </Dialog.Body>
    </ModalDialog>
  );
}
